import React, { useState, useEffect, useCallback } from 'react';
import Button from '../ui/Button';
import useNotification from '../../hooks/useNotification';
import axiosInstance from '../../api/Axios';
import { API_ENDPOINTS } from '../../constants/api';
import { TrashIcon, PlusIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmDeleteModal from '../modals/ConfirmDeleteModal';

const ProjectManagement = () => {
  const { showSuccess, showError } = useNotification();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newProjectName, setNewProjectName] = useState('');
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, projectId: null, projectName: '' });
  const [permanentDeleteModal, setPermanentDeleteModal] = useState({ isOpen: false, projectId: null, projectName: '' });

  const fetchProjects = useCallback(async () => {
    try {
      setError(null);
      const response = await axiosInstance.get(API_ENDPOINTS.GET_ALL_PROJECTS);
      setProjects(response.data);
    } catch (error) {
      setError(error.message || 'Ошибка при загрузке проектов');
      showError('Ошибка при загрузке проектов');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleAddProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.GET_PROJECTS, { name: newProjectName });
      setProjects([...projects, response.data]);
      setNewProjectName('');
      showSuccess('Проект успешно добавлен');
    } catch (error) {
      showError(error.response?.data?.descr || 'Ошибка при добавлении проекта');
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axiosInstance.delete(`${API_ENDPOINTS.DELETE_PROJECT}/${projectId}`);
      setProjects(projects.map(project => 
        (project.id || project._id) === projectId 
          ? { ...project, isActive: false }
          : project
      ));
      closeDeleteModal();
      showSuccess('Проект успешно деактивирован');
    } catch (error) {
      showError(error.response?.data?.error || 'Ошибка при деактивации проекта');
    }
  };

  const handleActivateProject = async (projectId) => {
    try {
      await axiosInstance.put(`${API_ENDPOINTS.GET_PROJECTS}/${projectId}/activate`);
      setProjects(projects.map(project => 
        (project.id || project._id) === projectId 
          ? { ...project, isActive: true }
          : project
      ));
      showSuccess('Проект успешно активирован');
    } catch (error) {
      showError(error.response?.data?.error || 'Ошибка при активации проекта');
    }
  };

  const handlePermanentDeleteProject = async (projectId) => {
    try {
      await axiosInstance.delete(`${API_ENDPOINTS.DELETE_PROJECT_PERMANENT}/${projectId}/permanent`);
      setProjects(projects.filter(project => (project.id || project._id) !== projectId));
      closePermanentDeleteModal();
      showSuccess('Проект полностью удален');
    } catch (error) {
      showError(error.response?.data?.error || 'Ошибка при удалении проекта');
    }
  };

  const openDeleteModal = (projectId, projectName) => {
    setDeleteModal({ isOpen: true, projectId, projectName });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, projectId: null, projectName: '' });
  };

  const openPermanentDeleteModal = (projectId, projectName) => {
    setPermanentDeleteModal({ isOpen: true, projectId, projectName });
  };

  const closePermanentDeleteModal = () => {
    setPermanentDeleteModal({ isOpen: false, projectId: null, projectName: '' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Загрузка проектов...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          <div className="font-semibold mb-2">Ошибка загрузки:</div>
          <div>{error}</div>
          <button 
            onClick={fetchProjects}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  const activeProjects = projects.filter(project => project.isActive !== false);
  const inactiveProjects = projects.filter(project => project.isActive === false);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Управление проектами</h2>
          <div className="flex gap-2">
            <input
              type="text"
              value={newProjectName}
              onChange={e => setNewProjectName(e.target.value)}
              placeholder="Название проекта"
              className="border rounded px-3 py-1 text-sm"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddProject}
              className="flex items-center"
              disabled={!newProjectName.trim()}
            >
              <PlusIcon className="w-4 h-4 mr-1" />Добавить
            </Button>
          </div>
        </div>

        {/* Активные проекты */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <CheckIcon className="w-5 h-5 mr-2 text-green-500" />
            Активные проекты ({activeProjects.length})
          </h3>
          {activeProjects.length === 0 ? (
            <div className="text-center text-gray-500 py-8">Нет активных проектов</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 font-medium text-gray-700">Название проекта</th>
                    <th className="text-left p-3 font-medium text-gray-700">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {activeProjects.map((project) => (
                    <tr key={project.id || project._id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3">
                        <span className="font-medium text-gray-900">{project.name}</span>
                      </td>
                      <td className="p-3">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => openDeleteModal(project.id || project._id, project.name)}
                          className="flex items-center"
                        >
                          <TrashIcon className="w-4 h-4 mr-1" />
                          Деактивировать
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Деактивированные проекты */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <XMarkIcon className="w-5 h-5 mr-2 text-red-500" />
            Деактивированные проекты ({inactiveProjects.length})
          </h3>
          {inactiveProjects.length === 0 ? (
            <div className="text-center text-gray-500 py-8">Нет деактивированных проектов</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 font-medium text-gray-700">Название проекта</th>
                    <th className="text-left p-3 font-medium text-gray-700">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {inactiveProjects.map((project) => (
                    <tr key={project.id || project._id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3">
                        <span className="font-medium text-gray-500 line-through">{project.name}</span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleActivateProject(project.id || project._id)}
                            className="flex items-center"
                          >
                            <CheckIcon className="w-4 h-4 mr-1" />
                            Активировать
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => openPermanentDeleteModal(project.id || project._id, project.name)}
                            className="flex items-center"
                            disabled
                          >
                            <TrashIcon className="w-4 h-4 mr-1" />
                            Удалить
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Модальное окно деактивации */}
      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={() => handleDeleteProject(deleteModal.projectId)}
        title="Деактивация проекта"
        message={`Вы уверены, что хотите деактивировать проект "${deleteModal.projectName}"? Проект исчезнет из списка активных, но все связанные задачи останутся.`}
      />
      
      {/* Модальное окно полного удаления */}
      <ConfirmDeleteModal
        isOpen={permanentDeleteModal.isOpen}
        onClose={closePermanentDeleteModal}
        onConfirm={() => handlePermanentDeleteProject(permanentDeleteModal.projectId)}
        title="Полное удаление проекта"
        message={`Вы уверены, что хотите полностью удалить проект "${permanentDeleteModal.projectName}"? Это действие нельзя отменить. Проект будет удален навсегда.`}
      />
    </div>
  );
};

export default ProjectManagement; 